// Table — a NEW Content block for the Wheel Builder (craft.js node).
// ISP-4018 (Fable Hail Mary Wave 5). Deck: concepts/builder-blocks/02-table-block-editor.
//
// Renders a REAL semantic <table> (caption + <th scope="col"> + striped tbody).
// Deck display rule: 4 rows show in the Segment; longer tables scroll in-place
// with the header row PINNED (position: sticky thead). Many columns scroll
// horizontally — the page body never overflows. Numeric columns right-align.
//
// The editable model lives under a single `table` prop (see ./wiring.js). The
// settings adapter (TableEditorSettings) reads the node via useNode, hands the
// normalized value to the presentational <TableEditor>, and persists edits with
// setProp — every mutation routed through the pure grid ops so the editor and
// this render can never desync on row/column counts. setProp gives craft undo/redo.

import { useNode } from "@craftjs/core";
import React, { useMemo } from "react";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import TableEditor from "../../BlockEditorPane/editors/TableEditor";
import TableView from "./TableView";
import wiring from "./wiring";

export { TableView };

export const Table = ({ table, margin, ...props }) => {
  const {
    connectors: { connect, drag },
  } = useNode();
  const { getPaletteColor } = usePaletteColor();
  const m = margin || {};
  const marginStyle = `${m.top || 0}px ${m.right || 0}px ${m.bottom || 0}px ${m.left || 0}px`;
  const value = useMemo(() => wiring.normalize(table), [table]);
  // ISP-9999: TableView stays craft/redux-free (it's shared with the read-only
  // View Wheel render and unit tests), so the symbolic default/override is
  // resolved to a real hex HERE, once, before it reaches TableView.
  const resolvedValue = { ...value, headerColor: getPaletteColor(value.headerColor) };

  return (
    <div
      {...props}
      ref={(ref) => connect(drag(ref))}
      style={{ margin: marginStyle }}
      data-testid="table-block-wrapper"
    >
      <BlockIndicator>
        <TableView value={resolvedValue} />
      </BlockIndicator>
    </div>
  );
};

// ── Settings adapter: connects the presentational editor to the craft node ────
const TableEditorSettings = () => {
  const {
    table,
    margin,
    actions: { setProp },
  } = useNode((node) => ({
    table: node.data.props.table,
    margin: node.data.props.margin,
  }));

  // ISP-9999: hand TableEditor the RAW value (sentinel-or-literal) — never a
  // pre-resolved hex. ColorPopup resolves "suggested_color_0" to a real color
  // itself (see its own getPaletteColor(background) call) purely for display.
  // Baking the resolution in HERE would round-trip through TableEditor's
  // onChange on the NEXT unrelated edit (add a row, rename a header cell…)
  // and freeze the sentinel into a literal hex, silently breaking
  // live-tracking for a block the user never touched the color of.
  const value = wiring.nodePropsToValue({ table });

  const handleChange = (nextValue) => {
    setProp((props) => {
      props.table = wiring.normalize(nextValue);
    });
  };

  const handleMarginChange = (side, next) => {
    setProp((props) => {
      props.margin = { top: 0, right: 0, bottom: 0, left: 0, ...props.margin, [side]: next };
    });
  };

  return (
    <TableEditor
      value={value}
      onChange={handleChange}
      margin={margin}
      onMarginChange={handleMarginChange}
    />
  );
};

// fix-kelly-0929-2018-editwheel-video-bugs: a Table has no internal padding of
// its own (see TableView.jsx — the <table> sits flush inside its scroll
// wrapper), unlike sibling content blocks (e.g. Graph, which pads its own
// content 8px/4px/6px). A margin of all-zero left two Table blocks placed
// back-to-back rendered with a literal 0px gap — the second header row butted
// directly against the first table's last row, reading as a corrupted/
// duplicated table rather than two distinct blocks (Kelly, Slack #devs
// 2026-09-29 20:18 PT, 02:18-02:36 in the reported video). Table has no
// margin UI control (TableEditorSettings never exposes one), so this default
// is the only spacing a Table block ever gets.
// fix-kelly-0930-P0: a newly-added Table starts EMPTY, not pre-filled with
// the Deck's demo "Career table" content -- see wiring.js's EMPTY export.
export const TableDefaultProps = {
  table: wiring.EMPTY,
  margin: { top: 0, right: 0, bottom: 16, left: 0 },
};

Table.craft = {
  displayName: "Table",
  props: TableDefaultProps,
  related: {
    settings: TableEditorSettings,
  },
};

export default Table;
